// src/pages/dashboard/branch/categories/components/AddCategoryModal.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiX, FiUpload, FiEye, FiEyeOff, FiChevronLeft, FiChevronRight, FiLayers } from 'react-icons/fi';
import Button from '../../../../../components/ui/Button';
import InputField from '../../../../../components/ui/InputField';
import { Category, insertCategory, supabase } from '../../../../../utils/supabaseClient';
import { uploadToSupabaseStorage } from '../../../../../utils/supabaseStorage';
import Toggle from '../../../../../components/ui/Toggle';
import { getCategoryName } from '../../../../../utils/categoryHelper';

import { useAppDispatch } from '../../../../../store/hooks'
import { createNewCategory } from '../../../../../store/slices/categorySlice'

interface AddCategoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCategoryAdded: (category: Category) => void;
	branchId: string;
	mainCategories: Category[]; // For parent category selection
	selectedParentId?: string;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
	isOpen,
	onClose,
	onCategoryAdded,
	branchId,
	mainCategories,
	selectedParentId
}) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';

	const dispatch = useAppDispatch()

	const [formData, setFormData] = useState({
		name: '',
		description: '',
		arName: '',
		arDescription: '',
		parentId: selectedParentId || '',
		isPublished: true
	});

	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setImageFile(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setImagePreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const removeImage = () => {
		setImageFile(null);
		setImagePreview(null);
	};

	const validateForm = () => {
		const newErrors: string[] = [];

		if (!formData.name.trim()) {
			newErrors.push(t('categories.errors.nameRequired'));
		}

		if (!formData.arName.trim()) {
			newErrors.push(t('categories.errors.arNameRequired'));
		}

		return newErrors;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		const validationErrors = validateForm();
		if (validationErrors.length > 0) {
			setError(validationErrors[0]);
			return;
		}

		setIsLoading(true);

		try {
			// Upload image if provided
			let imageUrl = null;
			if (imageFile) {
				const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${imageFile.name}`;
				const uploadResult = await uploadToSupabaseStorage(imageFile, uniqueFileName, {
					bucket: 'uploads',
					path: `categories/${branchId}`
				});
				imageUrl = uploadResult.publicUrl;
			}

			const dataToSave = {
				name: formData.name.trim(),
				description: formData.description.trim() || null,
				branch_id: branchId,
				parent_id: formData.parentId || null,
				is_published: formData.isPublished,
				image_url: imageUrl,
				// Store Arabic translations
				ar: {
					name: formData.arName.trim(),
					description: formData.arDescription.trim() || null
				}
			};

			// const result = await insertCategory(dataToSave);

			// if (!result) return

			// if (!result.success) console.log(result.message);

			// onCategoryAdded(result.data);

			const result = await dispatch(createNewCategory(dataToSave)).unwrap();
			// Redux already updated state, notify parent
			onCategoryAdded(result);
			handleClose();
		} catch (err) {
			console.error('Error creating category:', err);
			setError(t('categories.errors.createFailed'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setFormData({
			name: '',
			description: '',
			arName: '',
			arDescription: '',
			parentId: selectedParentId || '',
			isPublished: true
		});
		setImageFile(null);
		setImagePreview(null);
		setError(null);
		onClose();
	};

	const modalVariants = {
		hidden: { opacity: 0, scale: 0.95 },
		visible: { opacity: 1, scale: 1 },
		exit: { opacity: 0, scale: 0.95 }
	};

	const overlayVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
		exit: { opacity: 0 }
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
						<motion.div
							variants={overlayVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							className="fixed inset-0 bg-black/50"
							onClick={handleClose}
						/>

						<motion.div
							variants={modalVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							transition={{ type: "spring", duration: 0.3 }}
							className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-start align-middle bg-white dark:bg-gray-800 rounded-lg shadow-xl relative z-10"
							onClick={(e) => e.stopPropagation()}
						>
							{/* Header */}
							<div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
								<h3 className="text-lg font-medium text-gray-900 dark:text-white">
									{t('categories.addCategory')}
								</h3>
								<button
									onClick={handleClose}
									className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
								>
									<FiX size={20} />
								</button>
							</div>

							{/* Form */}
							<form onSubmit={handleSubmit} className="mt-6">
								{error && (
									<motion.div
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm"
									>
										{error}
									</motion.div>
								)}

								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									{/* Left Column - English Fields */}
									<div className="flex flex-col gap-4">
										<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-2">
											{t('categories.englishDetails')}
										</h4>

										{/* English Name */}
										<InputField
											label={t('categories.enName')}
											name="name"
											value={formData.name}
											onChange={handleInputChange}
											placeholder={t('categories.namePlaceholder')}
											required
										/>

										{/* English Description */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
												{t('categories.enDescription')}
											</label>
											<textarea
												name="description"
												value={formData.description}
												onChange={handleInputChange}
												placeholder={t('categories.descriptionPlaceholder')}
												rows={4}
												className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
											/>
										</div>

										{/* Parent Category */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
												{t('categories.parentCategory')}
											</label>
											<select
												disabled={!!selectedParentId}
												name="parentId"
												value={formData.parentId}
												onChange={handleInputChange}
												className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
											>
												<option value="">{t('categories.mainCategory')}</option>
												{mainCategories?.map((category) => (
													<option key={category.id} value={category.id}>
														{getCategoryName(category, i18n.language)}
													</option>
												))}
											</select>
										</div>

										{/* Published Toggle */}
										<div className="flex items-center justify-between mt-3">
											<div className="flex items-center gap-3">
												{formData.isPublished ? (
													<FiEye className="w-5 h-5 text-green-500" />
												) : (
													<FiEyeOff className="w-5 h-5 text-gray-400" />
												)}
												<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
													{t('categories.published')}
												</span>
											</div>
											<Toggle
												enabled={formData.isPublished}
												onChange={() => setFormData(prev => ({ ...prev, isPublished: !prev.isPublished }))}
											/>
										</div>
									</div>

									{/* Right Column - Arabic Fields & Image */}
									<div className="flex flex-col gap-4">
										<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-2">
											{t('categories.arabicDetails')}
										</h4>

										{/* Arabic Name */}
										<InputField
											label={t('categories.arName')}
											name="arName"
											value={formData.arName}
											onChange={handleInputChange}
											placeholder={t('categories.arNamePlaceholder')}
											required
											dir='rtl'
											className="" // RTL text alignment for Arabic
										/>

										{/* Arabic Description */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
												{t('categories.arDescription')}
											</label>
											<textarea
												dir='rtl'
												name="arDescription"
												value={formData.arDescription}
												onChange={handleInputChange}
												placeholder={t('categories.arDescriptionPlaceholder')}
												rows={4}
												className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none" // RTL text alignment
											/>
										</div>

										{/* Image Upload */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
												{t('categories.image')}
											</label>

											{!imagePreview ? (
												<label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
													<div className="flex flex-col items-center justify-center pt-5 pb-6">
														<FiUpload className="w-6 h-6 mb-2 text-gray-400" />
														<p className="text-xs text-gray-500 dark:text-gray-400">
															{t('categories.uploadImage')}
														</p>
													</div>
													<input
														type="file"
														accept="image/*"
														onChange={handleImageChange}
														className="hidden"
													/>
												</label>
											) : (
												<div className="relative">
													<img
														src={imagePreview}
														alt="Preview"
														className="w-full h-32 object-cover rounded-lg"
													/>
													<button
														type="button"
														onClick={removeImage}
														className="absolute top-2 end-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors cursor-pointer"
													>
														<FiX size={12} />
													</button>
												</div>
											)}
										</div>
									</div>
								</div>

								{/* Actions */}
								<div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
									<Button
										type="button"
										variant="outline"
										onClick={handleClose}
										disabled={isLoading}
										className="flex-1"
									>
										{t('common.cancel')}
									</Button>
									<Button
										type="submit"
										isLoading={isLoading}
										disabled={!formData.name.trim() || !formData.arName.trim()}
										className="flex-1"
										leftIcon={<FiLayers />}
										rightIcon={isRTL ? <FiChevronLeft /> : <FiChevronRight />}
									>
										{t('categories.addCategory')}
									</Button>
								</div>
							</form>
						</motion.div>
					</div>
				</div>
			)}
		</AnimatePresence>
	);
};

export default AddCategoryModal;